import { FC, useCallback, useEffect, useRef, useState } from 'react';

type ResizableDividerProps = {
  onResize: (leftFraction: number) => void;
};

export const ResizableDivider: FC<ResizableDividerProps> = ({ onResize }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dividerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const parent = dividerRef.current?.parentElement;
      if (!parent) return;

      const rect = parent.getBoundingClientRect();
      if (rect.width <= 0) return;
      const fraction = Math.min(0.8, Math.max(0.2, (e.clientX - rect.left) / rect.width));
      onResize(fraction);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, onResize]);

  return (
    <div
      ref={dividerRef}
      onMouseDown={handleMouseDown}
      style={{
        width: '6px',
        cursor: 'col-resize',
        backgroundColor: isDragging ? 'rgba(43, 143, 119, 0.5)' : 'transparent',
        borderLeft: '1px solid rgba(81, 81, 81, 0.5)',
        borderRight: '1px solid rgba(81, 81, 81, 0.5)',
        flexShrink: 0,
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(43, 143, 119, 0.3)';
      }}
      onMouseLeave={(e) => {
        if (!isDragging) {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
        }
      }}
    />
  );
};
